import { Check, ChevronDown, CircleHelp } from "lucide-react"
import { useEffect, useRef, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"
import { MENU_GAP_PX, useDropdownMenu, useFitContentWidth } from "@/components/ui/dropdown-menu-position"
import { DropdownSearchInput } from "@/components/ui/dropdown-search-input"
import { cn } from "@/lib/utils"

export type SelectDropdownOptionAction = {
  icon: ReactNode
  ariaLabel: string
  className?: string
  wrapClassName?: string
  onSelect: () => void
}

export type SelectDropdownOption = {
  value: string
  label: string
  /** Значок слева от подписи — в меню и на кнопке. */
  icon?: ReactNode
  disabled?: boolean
  /** Подсказка рядом с опцией (иконка «?» + тултип слева от меню). */
  hint?: string
  /** Разделитель под опцией — отделяет особый пункт (например «по умолчанию») от списка. */
  separatorAfter?: boolean
  /** Иконки справа от лейбла (rename/delete). Клик не выбирает опцию. */
  actions?: SelectDropdownOptionAction[]
}

export type SelectDropdownProps = {
  value: string
  onChange: (value: string) => void
  options: SelectDropdownOption[]
  disabled?: boolean
  className?: string
  buttonClassName?: string
  dropdownDirection?: "up" | "down"
  ariaLabel?: string
  placeholder?: string
  /**
   * Ширина кнопки = самая длинная опция (+ шеврон/паддинги), без растягивания на всю строку.
   * Меню не уже кнопки; длинные пункты не клипаются по ширине триггера.
   */
  fitContent?: boolean
  /** Строка поиска сверху меню; фильтр по подстроке лейбла без учёта регистра. */
  searchable?: boolean
  searchPlaceholder?: string
  /** Текст пустого результата поиска. */
  searchEmptyLabel?: string
  /** Триггер без рамки: текущая подпись + шеврон (хедер холста). Меню по ширине опций. */
  iconOnly?: boolean
  /** xs: h-6 text-xs. sm: h-8 text-xs (тулбары). md: h-10 text-sm, как Input. */
  size?: SelectDropdownSize
  /** Галочка слева только у выбранной строки меню, не на закрытой кнопке. */
  showSelectedCheck?: boolean
  /** Кнопки строки видны по наведению и с клавиатуры (focus-within). */
  actionsOnHover?: boolean
  /** Явная горизонтальная привязка меню. Не переключается на другой край. */
  menuAlign?: "left" | "right"
}

/** xs — плотная строка; sm — тулбары и фильтры; md — поле формы, как Input. */
export type SelectDropdownSize = "xs" | "sm" | "md"

/** Выше меню — тултип подсказки роли. */
const HINT_Z_INDEX = 250
/** horizontal padding + gap-1 + chevron (14) + border (2). xs/sm: px-2, md: px-3. */
const FIT_CONTENT_CHROME_PX: Record<SelectDropdownSize, number> = {
  xs: 8 + 8 + 4 + 14 + 2,
  sm: 8 + 8 + 4 + 14 + 2,
  md: 12 + 12 + 4 + 14 + 2,
}
/** gap-2 + hitbox кнопки подсказки (h-7/w-7). */
const FIT_CONTENT_HINT_EXTRA_PX = 8 + 28
/** Иконка опции (14) + её pr-1 + gap-1 до подписи. Без этого fitContent обрезает текст. */
const FIT_CONTENT_ICON_EXTRA_PX = 14 + 4 + 4
const HINT_SHOW_DELAY_MS = 150

type HintPopover = {
  text: string
  /** left edge of the dropdown menu (viewport). */
  menuLeft: number
  /** vertical center of the option row (viewport). */
  rowCenterY: number
}

export function SelectDropdown({
  value,
  onChange,
  options,
  disabled = false,
  className,
  buttonClassName,
  dropdownDirection = "down",
  ariaLabel,
  placeholder,
  fitContent = false,
  searchable = false,
  searchPlaceholder,
  searchEmptyLabel,
  iconOnly = false,
  size = "sm",
  showSelectedCheck = false,
  actionsOnHover = false,
  menuAlign,
}: SelectDropdownProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [hintPopover, setHintPopover] = useState<HintPopover | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)
  const measureRef = useRef<HTMLSpanElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const hintShowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const selected = options.find((o) => o.value === value)
  const hasHints = options.some((o) => Boolean(o.hint))
  const hasIcons = options.some((o) => o.icon != null)
  const normalizedQuery = query.trim().toLowerCase()
  const visibleOptions =
    searchable && normalizedQuery ? options.filter((o) => o.label.toLowerCase().includes(normalizedQuery)) : options

  const clearHintShowTimer = () => {
    if (hintShowTimerRef.current == null) return
    clearTimeout(hintShowTimerRef.current)
    hintShowTimerRef.current = null
  }

  const hideHint = () => {
    clearHintShowTimer()
    setHintPopover(null)
  }

  const scheduleHint = (rowEl: HTMLElement, text: string) => {
    clearHintShowTimer()
    hintShowTimerRef.current = setTimeout(() => {
      const menu = menuRef.current
      if (!menu) return
      const menuRect = menu.getBoundingClientRect()
      const rowRect = rowEl.getBoundingClientRect()
      const dialog = menu.closest('[role="dialog"]')
      const frame = dialog instanceof HTMLElement ? dialog.getBoundingClientRect() : null
      setHintPopover({
        text,
        // Fixed coords must match the portal containing block (dialog may have transform).
        menuLeft: frame ? menuRect.left - frame.left : menuRect.left,
        rowCenterY: frame ? rowRect.top + rowRect.height / 2 - frame.top : rowRect.top + rowRect.height / 2,
      })
    }, HINT_SHOW_DELAY_MS)
  }

  useEffect(() => {
    if (open) return
    hideHint()
    setQuery("")
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only clear when menu closes
  }, [open])

  useEffect(() => {
    if (!open || !searchable) return
    // Меню монтируется после расчёта позиции — фокус переносим кадром позже.
    const id = requestAnimationFrame(() => searchInputRef.current?.focus())
    return () => cancelAnimationFrame(id)
  }, [open, searchable])

  useEffect(() => () => clearHintShowTimer(), [])

  const fitWidthPx = useFitContentWidth({
    enabled: fitContent,
    labels: placeholder ? [...options.map((o) => o.label), placeholder] : options.map((o) => o.label),
    chromePx: FIT_CONTENT_CHROME_PX[size] + (hasIcons ? FIT_CONTENT_ICON_EXTRA_PX : 0),
    anchorRef: buttonRef,
    probeRef: measureRef,
  })

  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    disabled,
    direction: dropdownDirection,
    align: menuAlign ?? (iconOnly ? "left" : "right"),
    pinAlign: menuAlign != null,
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    positionKey: `${fitContent}:${fitWidthPx}:${hasHints}:${iconOnly}`,
    resolveWidth: (rect) => {
      if (iconOnly) {
        return {
          minWidth: 12 * 16,
          width: "max-content",
          maxWidth: "min(24rem, calc(100vw - 16px))",
        }
      }
      if (!fitContent) return { minWidth: rect.width }

      // Меню по контенту, не уже триггера; длинные пункты целиком (+ место под «?» при hints).
      return {
        minWidth: Math.max(rect.width, (fitWidthPx ?? 0) + (hasHints ? FIT_CONTENT_HINT_EXTRA_PX : 0)),
        width: "max-content",
        maxWidth: "min(24rem, calc(100vw - 16px))",
      }
    },
    onReposition: () => {
      // Пересчитать тултип относительно меню после скролла/ресайза.
      setHintPopover((prev) => {
        if (!prev) return null
        const menu = menuRef.current
        if (!menu) return prev
        const menuRect = menu.getBoundingClientRect()
        const dialog = menu.closest('[role="dialog"]')
        const frame = dialog instanceof HTMLElement ? dialog.getBoundingClientRect() : null
        return {
          ...prev,
          menuLeft: frame ? menuRect.left - frame.left : menuRect.left,
        }
      })
    },
    onOutsideClick: () => setOpen(false),
  })

  const hintPortal =
    hintPopover && portalRoot
      ? createPortal(
          <div
            role="tooltip"
            style={{
              position: "fixed",
              left: hintPopover.menuLeft - MENU_GAP_PX,
              top: hintPopover.rowCenterY,
              transform: "translate(-100%, -50%)",
              zIndex: HINT_Z_INDEX,
            }}
            className={cn(
              // w-max: иначе shrink-to-fit у fixed+left берёт «доступную» ширину справа от меню и текст рано переносится.
              "pointer-events-none w-max max-w-[min(24rem,calc(100vw-1.5rem))] rounded-lg border border-ui-border bg-ui-bg-elevated px-3.5 py-2.5 text-xs leading-relaxed break-words whitespace-pre-wrap text-ui-text-primary shadow-[var(--ui-shadow-md)]",
              "animate-in fade-in-0 zoom-in-95 slide-in-from-right-1",
            )}
          >
            {hintPopover.text}
          </div>,
          portalRoot,
        )
      : null

  const menu =
    open && !disabled && menuStyle && portalRoot ? (
      <ul
        ref={menuRef}
        role="listbox"
        data-panel-wheel-isolate
        aria-label={ariaLabel ?? selected?.label ?? placeholder}
        style={menuStyle}
        className={cn(
          "max-h-60 overflow-auto rounded-lg border border-ui-border bg-ui-bg-elevated shadow-xl shadow-black/40",
          searchable ? "pb-1" : "py-1",
        )}
        onScroll={hideHint}
      >
        {searchable ? (
          <li role="none" className="sticky top-0 z-10">
            <DropdownSearchInput
              inputRef={searchInputRef}
              value={query}
              onChange={setQuery}
              placeholder={searchPlaceholder}
              ariaLabel={searchPlaceholder ?? ariaLabel}
              onDismiss={() => setOpen(false)}
              onCommitFirst={() => {
                const first = visibleOptions.find((o) => !o.disabled)
                if (first) {
                  onChange(first.value)
                  setOpen(false)
                }
              }}
            />
          </li>
        ) : null}
        {searchable && visibleOptions.length === 0 ? (
          <li className="px-3 py-2 text-xs text-ui-text-muted">{searchEmptyLabel ?? "—"}</li>
        ) : null}
        {visibleOptions.map((option, index) => (
          <li
            key={`${option.value}-${index}`}
            role="option"
            aria-selected={value === option.value}
            aria-disabled={option.disabled}
            onMouseDown={(e) => {
              e.preventDefault()
              if (option.disabled) return
              onChange(option.value)
              setOpen(false)
            }}
            className={cn(
              "flex min-h-8 cursor-pointer items-center gap-2 px-3 py-1.5 text-xs transition-colors",
              "hover:bg-ui-accent/25 light:hover:bg-[var(--ui-nav-active-bg)]",
              actionsOnHover && "group",
              value === option.value
                ? "bg-ui-accent/25 text-ui-accent-text light:bg-[var(--ui-nav-active-bg)]"
                : "text-ui-text-primary",
              option.disabled && "cursor-not-allowed opacity-40",
              option.separatorAfter && "border-b border-ui-border",
            )}
          >
            {showSelectedCheck ? (
              value === option.value ? (
                <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
              ) : (
                <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
              )
            ) : null}
            {option.icon}
            <span className={cn("min-w-0 flex-1", fitContent ? "whitespace-nowrap" : "truncate")}>{option.label}</span>
            {option.actions && option.actions.length > 0 ? (
              <span
                className={cn(
                  "ml-3 flex shrink-0 items-center gap-0.5",
                  actionsOnHover &&
                    "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100",
                  actionsOnHover && "group-focus-within:pointer-events-auto group-focus-within:opacity-100",
                )}
              >
                {option.actions.map((action, actionIndex) => (
                  <span key={`${option.value}-action-${actionIndex}`} className={cn("shrink-0", action.wrapClassName)}>
                    <button
                      type="button"
                      aria-label={action.ariaLabel}
                      className={cn(
                        "shrink-0 focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none",
                        action.className ??
                          "inline-flex h-6 w-6 items-center justify-center rounded-md text-ui-text-muted hover:bg-ui-surface-sunken hover:text-ui-text-primary",
                      )}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        if (option.disabled) return
                        setOpen(false)
                        action.onSelect()
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {action.icon}
                    </button>
                  </span>
                ))}
              </span>
            ) : null}
            {option.hint ? (
              <button
                type="button"
                className="-m-1.5 inline-flex h-7 w-7 shrink-0 items-center justify-center self-center rounded-md leading-none text-ui-text-muted hover:text-ui-text-secondary focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
                aria-label={option.hint.split("\n")[0] ?? option.label}
                onMouseDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseEnter={(e) => {
                  const row = e.currentTarget.closest('[role="option"]')
                  if (row instanceof HTMLElement && option.hint) scheduleHint(row, option.hint)
                }}
                onMouseLeave={hideHint}
                onFocus={(e) => {
                  const row = e.currentTarget.closest('[role="option"]')
                  if (row instanceof HTMLElement && option.hint) scheduleHint(row, option.hint)
                }}
                onBlur={hideHint}
              >
                <CircleHelp className="block h-3 w-3" />
              </button>
            ) : hasHints ? (
              <span className="-m-1.5 inline-flex h-7 w-7 shrink-0 self-center" aria-hidden />
            ) : null}
          </li>
        ))}
      </ul>
    ) : null

  return (
    <div
      ref={rootRef}
      className={cn("relative", fitContent && "max-w-full shrink-0", className)}
      style={fitContent && fitWidthPx != null ? { width: fitWidthPx, maxWidth: "100%" } : undefined}
    >
      {fitContent ? (
        <span
          ref={measureRef}
          aria-hidden
          className={cn(
            "pointer-events-none invisible absolute top-0 left-0 whitespace-nowrap",
            size === "md" ? "text-sm" : "text-xs leading-tight",
          )}
        />
      ) : null}
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel ?? selected?.label ?? placeholder}
        onClick={() => {
          if (disabled) return
          setOpen((prev) => !prev)
        }}
        className={cn(
          iconOnly
            ? "inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-sm border-0 bg-transparent p-0 font-medium text-ui-text-primary"
            : cn(
                "inline-flex w-full items-center gap-1 border border-ui-border bg-ui-surface-sunken py-0 text-ui-text-primary",
                size === "md" && "h-10 rounded-lg px-3 text-sm",
                size === "sm" && "h-8 rounded-lg px-2 text-xs leading-tight",
                size === "xs" && "h-6 rounded-md px-2 text-xs leading-tight",
                fitContent ? "min-w-0" : "min-w-[8rem]",
              ),
          "focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none",
          disabled && "pointer-events-none opacity-50",
          buttonClassName,
        )}
      >
        {selected?.icon ? <span className="flex shrink-0 items-center pr-1">{selected.icon}</span> : null}
        <span className={iconOnly ? "min-w-0 truncate text-left" : "min-w-0 flex-1 truncate text-left leading-tight"}>
          {selected?.label ?? placeholder ?? ""}
        </span>
        <ChevronDown className={cn("h-3.5 w-3.5 shrink-0", !iconOnly && "text-ui-text-muted", open && "rotate-180")} />
      </button>
      {menu && portalRoot ? createPortal(menu, portalRoot) : null}
      {hintPortal}
    </div>
  )
}
